import React, { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { careerDetails } from '../data/careerDetails';
import { careerChapters } from '../data/career';
import { parseLinkedInLinks } from '../utils/linkedInLinks';
import Header from './Header';
import Footer from './Footer';
import FloatingIcons from './FloatingIcons';

function CareerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const career = id ? careerDetails[id] : null;

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Calculate previous and next chapter
  // Note: Career chapters are in descending order (most recent first)
  // So "previous" means more recent (lower index), "next" means older (higher index)
  const { prevChapter, nextChapter } = useMemo(() => {
    if (!id) return { prevChapter: null, nextChapter: null };
    
    const currentIndex = careerChapters.findIndex(ch => ch.id === id);
    if (currentIndex === -1) return { prevChapter: null, nextChapter: null };
    
    // Previous = more recent chapter (lower index, earlier in array)
    const prevChapter = currentIndex > 0 ? careerChapters[currentIndex - 1] : null;
    // Next = older chapter (higher index, later in array)
    const nextChapter = currentIndex < careerChapters.length - 1 ? careerChapters[currentIndex + 1] : null;
    
    return { prevChapter, nextChapter };
  }, [id]);

  if (!career) {
    return (
      <div className="App">
        <Header />
        <div className="container" style={{ padding: '80px 24px', textAlign: 'center' }}>
          <h2>Career Chapter Not Found</h2>
          <Link to="/" className="cta-button primary" style={{ marginTop: '24px', display: 'inline-block' }}>
            Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  // Split content into paragraphs
  const paragraphs = career.content.split('\n\n').filter(p => p.trim());

  return (
    <div className="App">
      <FloatingIcons />
      <Header />
      <section className="career-detail-section">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/#career" className="career-back-link">
              <ArrowLeft size={20} />
              <span>Back to Career</span>
            </Link>
          </motion.div>

          <motion.div
            className="career-detail-header"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <div className="career-detail-logo">
              <img src={career.logo} alt={`${career.name} logo`} />
            </div>
            <div className="career-detail-title-section">
              <h1 className="career-detail-name">{career.name}</h1>
              <p className="career-detail-title">{career.title}</p>
            </div>
          </motion.div>

          {career.image && (
            <motion.div
              className="career-detail-image-wrapper"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img src={career.image} alt={career.name} className="career-detail-image" />
            </motion.div>
          )}

          <motion.div
            className="career-detail-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            {paragraphs.map((paragraph, index) => {
              const parsedContent = parseLinkedInLinks(paragraph);
              return (
                <p key={`${career.id}-para-${index}`} className="career-detail-paragraph">
                  {parsedContent}
                </p>
              );
            })}
          </motion.div>

          {/* Previous/Next Navigation */}
          {(nextChapter || prevChapter) && (
            <motion.div
              className="career-detail-navigation"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              {nextChapter ? (
                <Link to={`/career/${nextChapter.id}`} className="career-nav-link career-nav-prev">
                  <ChevronLeft size={20} />
                  <div className="career-nav-content">
                    <span className="career-nav-label">Previous Chapter</span>
                    <span className="career-nav-name">{nextChapter.name}</span>
                  </div>
                </Link>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}
              
              {prevChapter ? (
                <Link to={`/career/${prevChapter.id}`} className="career-nav-link career-nav-next">
                  <div className="career-nav-content">
                    <span className="career-nav-label">Next Chapter</span>
                    <span className="career-nav-name">{prevChapter.name}</span>
                  </div>
                  <ChevronRight size={20} />
                </Link>
              ) : (
                <div style={{ flex: 1 }}></div>
              )}
            </motion.div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

export default CareerDetailPage;

